import React from 'react'
import PropTypes from 'prop-types'
import CircularProgress from 'material-ui/CircularProgress'

import AddBillingItemDialog from './AddBillingItemDialog'
import NewBillingItemDialog from './NewBillingItemDialog'
import NewMaterialDialog from './NewMaterialDialog'
import CommonRepository from 'billing/infra/CommonRepository'
import Utils from 'billing/infra/Utils'

const BillItemsHeader = () => {
  return (
    <div style={styles.headerRow}>
      <div style={styles.itemColumn(true, 250)}>ITEMS</div>
      <div style={styles.itemColumn(true, null, 'center')}>QUANTITY</div>
      <div style={styles.itemColumn(true, null, 'center')}>RATE</div>
      <div style={styles.itemColumn(true, null, 'center')}>TAX %</div>
      <div style={styles.itemColumn(true, null, 'right')}>AMOUNT</div>
      <div style={styles.itemColumn(true, 64, 'center')}></div>
    </div>
  )
}

const handleItemQuantityChanged = (event, item, onItemQuantityChanged) => {
  let quantity = parseInt(event.target.value)
  if (!quantity || isNaN(quantity)) {
    quantity = 1
  }
  onItemQuantityChanged(item, quantity)
}

const handleItemRateChanged = (event, item, onItemRateChanged) => {
  let rate = parseFloat(event.target.value)
  if (!rate || isNaN(rate)) {
    rate = .1
  }
  onItemRateChanged(item, rate)
}

const handleItemTaxPercentChanged = (event, item, onItemTaxPercentChanged) => {
  let taxPercent = parseInt(event.target.value)
  if (!taxPercent || isNaN(taxPercent)) {
    taxPercent = 0
  }
  onItemTaxPercentChanged(item, taxPercent)
}

const BillItem = ({ item, billingEntity, onItemDeleteRequested,
  onItemNameChanged, onItemQuantityChanged, onItemRateChanged, onItemTaxPercentChanged,
  onItemDescriptionChanged }) => {
  const amount = Utils.getItemAmount(item).toFixed(2)
  return (
    <div style={styles.itemContainer}>
      <div style={styles.itemRow}>
        <div style={styles.itemColumn(false, 250)}>
          <input
            style={{ ...styles.input, ... styles.nameInput }}
            type='text'
            placeholder='Item name'
            onChange={event => onItemNameChanged(item, event.target.value)}
            value={item.name} />
        </div>
        <div style={styles.itemColumn(false, null, 'center')}>
          <input
            style={styles.input}
            type='number'
            step='1'
            onChange={event => handleItemQuantityChanged(event, item, onItemQuantityChanged)} 
            value={item.quantity} />
        </div>
        <div style={styles.itemColumn(false, null, 'center')}>
          <input
            style={styles.input}
            type='number'
            step='0.1'
            onChange={event => handleItemRateChanged(event, item, onItemRateChanged)}
            value={item.rate} />
        </div>
        <div style={styles.itemColumn(false, null, 'center')}>
          <input
            style={styles.input}
            type='number'
            step='1'
            onChange={event => handleItemTaxPercentChanged(event, item, onItemTaxPercentChanged)}
            value={item.taxPercent} />
        </div>
        <div style={styles.itemColumn(false, null, 'right')}>${ amount }</div>
        <div style={styles.itemColumn(false, 64, 'center')}
          onClick={() => onItemDeleteRequested(item)}
          title={`Delete ${billingEntity} item`}>
          <i className='material-icons' style={styles.action}>delete</i>
        </div>
      </div>
      <div style={styles.descriptionRow}>
        <textarea
          style={styles.descriptionInput}
          rows={3}
          type='text'
          placeholder='Item description'
          onChange={event => onItemDescriptionChanged(item, event.target.value)}
          value={item.description} />
      </div>
    </div>
  )
}

BillItem.propTypes = {
  item: PropTypes.object.isRequired,
  billingEntity: PropTypes.string.isRequired,
  onItemDeleteRequested: PropTypes.func.isRequired,
  onItemNameChanged: PropTypes.func.isRequired,
  onItemQuantityChanged: PropTypes.func.isRequired,
  onItemRateChanged: PropTypes.func.isRequired,
  onItemTaxPercentChanged: PropTypes.func.isRequired,
  onItemDescriptionChanged: PropTypes.func.isRequired
}

export default class BillItems extends React.Component {

  static propTypes = {
    billEditor: PropTypes.object.isRequired,
    billingEntity: PropTypes.string.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      showAddBillItemDialog: false,
      showNewBillItemDialog: false,
      showNewMaterialDialog: false
    }
  }

  handleAddItemClicked = event => {
    event.preventDefault()
    this.setState({
      showAddBillItemDialog: true,
      anchorElement: event.currentTarget
    })
  }

  getBillItemFromMaterial = material => {
    const price = material.cost && parseFloat(material.cost)
    return {
      name: material.name,
      rate: !isNaN(price) ? price : 0,
      quantity: 1
    }
  }

  handleMaterialSelected = material => {
    const item = this.getBillItemFromMaterial(material)
    this.setState({
      showAddBillItemDialog: false
    })
    this.props.billEditor.addItem(item)
  }

  handleBillItemSelected = item => {
    this.setState({
      showAddBillItemDialog: false
    })
    this.props.billEditor.addItem(item)
  }

  handleAddBillItemDialogCloseRequested = () => {
    this.setState({
      showAddBillItemDialog: false
    })
  }

  handleNewItemActionSelected = () => {
    this.setState({
      showNewBillItemDialog: true,
      showAddBillItemDialog: false
    })
  }

  handleNewMaterialActionSelected = () => {
    this.setState({
      showNewMaterialDialog: true,
      showAddBillItemDialog: false
    })
  }

  handleNewItemDialogCloseRequested = () => {
    this.setState({
      showNewMaterialDialog: false,
      showNewBillItemDialog: false
    })
  }

  handleNewBillItemSave = newBillItem => {
    this.setState({
      showNewBillItemDialog: false
    })
    newBillItem.rate = parseFloat(newBillItem.rate)
    if (isNaN(newBillItem.rate)) {
      newBillItem.rate = 0
    }
    newBillItem.quantity = parseInt(newBillItem.quantity) || 0
    if (isNaN(newBillItem.quantity)) {
      newBillItem.quantity = 0
    }
    CommonRepository.addDefaultBillItem(
      this.props.billEditor.getTeamId(),
      newBillItem)
    this.props.billEditor.addItem(newBillItem)
  }

  handleNewMaterialSave = newMaterial => {
    this.setState({
      showNewMaterialDialog: false
    })
    CommonRepository.addDefaultMaterial(
      this.props.billEditor.getTeamId(),
      newMaterial
    )
    this.props.billEditor.addItem(
      this.getBillItemFromMaterial(newMaterial)
    )
  }

  render() {
    const loading = this.props.billEditor.isLoadingItems()
    const items = this.props.billEditor.getItems() || []
    let total = 0, subtotal = 0, tax = 0
    let content
    if (loading) {
      content = <div style={styles.progress}>
        <CircularProgress thickness={2} />
      </div>
    }
    else
    if (items.length === 0) {
      content = <div style={styles.zeroItems}>No items have been added yet.</div>
    }
    else {
      content = items.map((item, index) =>
        <BillItem key={index}
          item={item}
          billingEntity={this.props.billingEntity}
          onItemDeleteRequested={this.props.billEditor.deleteItem}
          onItemNameChanged={this.props.billEditor.updateItemName}
          onItemDescriptionChanged={this.props.billEditor.updateItemDescription}
          onItemQuantityChanged={this.props.billEditor.updateItemQuantity}
          onItemTaxPercentChanged={this.props.billEditor.updateItemTaxPercent}
          onItemRateChanged={this.props.billEditor.updateItemRate} />)
      subtotal = items.filter(item => item.rate).reduce((sum, item) => sum + Utils.getItemNet(item), 0)
      total = items.filter(item => item.rate).reduce((sum, item) => sum + Utils.getItemAmount(item), 0)
      tax = total - subtotal
    }
    return (
      <div style={styles.container}>
        <BillItemsHeader />
        { content }
        { !loading &&
          <div>
            <div style={styles.addItemContainer}>
              <div style={styles.addItem} onClick={this.handleAddItemClicked}>
                <i className='material-icons' style={{ marginRight: 4 }}>add_circle</i> Add Item...
              </div>
            </div>
            <div style={styles.summary}>
              <div style={styles.summarySections}>
                <div style={styles.summaryRow()}>
                  <div style={styles.summaryLabel()}>SUBTOTAL</div>
                  <div style={styles.summaryPrice()}>${ subtotal.toFixed(2) }</div>
                </div>
                <div style={styles.summaryRow()}>
                  <div style={styles.summaryLabel()}>TAX</div>
                  <div style={styles.summaryPrice()}>${ tax.toFixed(2) }</div>
                </div>
                <div style={styles.summaryRow(true)}>
                  <div style={styles.summaryLabel(true)}>TOTAL</div>
                  <div style={styles.summaryPrice(true)}>${ total.toFixed(2) }</div>
                </div>
              </div>
            </div>
          </div>
        }
        <AddBillingItemDialog
          open={this.state.showAddBillItemDialog}
          anchorElement={this.state.anchorElement}
          onBillItemSelected={this.handleBillItemSelected}
          onMaterialSelected={this.handleMaterialSelected}
          onCloseRequested={this.handleAddBillItemDialogCloseRequested}
          onNewItemActionSelected={this.handleNewItemActionSelected} 
          onNewMaterialActionSelected={this.handleNewMaterialActionSelected} />
        <NewBillingItemDialog
          open={this.state.showNewBillItemDialog}
          onCloseRequested={this.handleNewItemDialogCloseRequested}
          onSaveItemClicked={this.handleNewBillItemSave} />
        <NewMaterialDialog
          open={this.state.showNewMaterialDialog}
          onCloseRequested={this.handleNewItemDialogCloseRequested}
          onSaveItemClicked={this.handleNewMaterialSave} />
      </div>
    )
  }

}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column'
  },
  addItemContainer: {
    display:'flex',
    justifyContent: 'flex-start',
    borderBottom: 'solid 1px #e7e8ec',
    paddingTop: 12,
    paddingBottom: 12
  },
  itemContainer: {
    display:'flex',
    flexDirection: 'column',
    borderBottom: 'solid 1px #e7e8ec',
    paddingTop: 12,
    paddingBottom: 12
  },
  headerRow: {
    display:'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: 'solid 1px #e7e8ec',
    paddingTop: 8,
    paddingBottom: 8
  },
  descriptionRow: {
    display:'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  itemRow: {
    display:'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 8
  },
  itemColumn: (header, width, textAlign) => ({
    color: header ? '#9da8bb' : '#4a4a4a',
    textAlign: textAlign || 'left',
    fontSize: 12,
    width: width || 64
  }),
  addItem: {
    cursor: 'pointer',
    fontSize: 14,
    color: '#356992',
    paddingTop: 0,
    paddingBottom: 0,
    display: 'flex',
    alignItems: 'center'
  },
  zeroItems: {
    textAlign: 'center',
    borderBottom: 'solid 1px #e7e8ec',
    fontSize: 14,
    paddingTop: 12,
    paddingBottom: 12
  },
  progress: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    margin: 22
  },
  summary: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    fontSize: 12
  },
  summarySections: {
    display: 'flex',
    flexDirection: 'column',
    width: '50%'
  },
  summaryRow: last => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottom: last ? 'none' : '1px solid rgb(231, 232, 236)',
    paddingTop: 12,
    paddingBottom: '12'
  }),
  summaryLabel: main => ({
    color: main ? '#1f2229' : '#9da8bb'
  }),
  summaryPrice: main => ({
    color: main ? '#1f2229' : '#4a4a4a',
    textAlign: 'right'
  }),
  input: {
    borderRadius: 4,
    border: 'solid 1px #e6e8ed',
    width: 64,
    height: 24,
    textAlign: 'center',
    outlineWidth: 2,
    color: '#4a4a4a'
  },
  nameInput: {
    width: 250,
    textAlign: 'left',
    paddingLeft: 4
  },
  descriptionInput: {
    borderRadius: 4,
    border: 'solid 1px #e6e8ed',
    color: '#4a4a4a',
    outlineWidth: 2,
    textAlign: 'left',
    width: '100%',
    padding: 4,
    resize: 'vertical'
  },
  action: {
    fontSize: 14,
    color: '#4a4a4a',
    cursor: 'pointer'
  }
}



// WEBPACK FOOTER //
// ./src/billing/common/modules/items/BillItems.js